import { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { useUser } from '@/contexts/UserContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useCustomers } from '@/hooks/useCustomers';
import { useWarehouses } from '@/hooks/useWarehouses';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { CopyButton } from '@/components/CopyButton';
import {
  Users,
  Plus,
  Search,
  Phone,
  Mail,
  MapPin,
  Edit,
  Trash2,
  UserPlus,
  ToggleLeft,
  ToggleRight,
  Package,
  Eye,
  Filter,
  X,
  Globe,
  Building,
  LayoutGrid,
  List
} from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { countries, phoneCountryCodes, getStatesForCountry, getCitiesForState } from '@/data/countries';

interface Customer {
  id: string;
  code: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  country: string;
  totalShipments: number;
  status: 'Active' | 'Inactive';
  createdAt: string;
  destinationWarehouse?: string; // Warehouse code
}

export default function Customers() {
  const { customers: apiCustomers, isLoading, createCustomer, updateCustomer, deleteCustomer } = useCustomers();
  const { warehouses: apiWarehouses } = useWarehouses();

  const customers = apiCustomers || [];
  const warehouses = apiWarehouses || [];

  const [searchTerm, setSearchTerm] = useState('');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isShipmentsDialogOpen, setIsShipmentsDialogOpen] = useState(false);
  const [selectedCustomerShipments, setSelectedCustomerShipments] = useState<any[]>([]);
  const [selectedCustomerName, setSelectedCustomerName] = useState('');
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);

  // Filter states
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [countryFilter, setCountryFilter] = useState<string>('all');
  const [cityFilter, setCityFilter] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    country: '',
    destinationWarehouse: ''
  });
  
  // Cascading dropdowns state
  const [availableStates, setAvailableStates] = useState<any[]>([]);
  const [availableCities, setAvailableCities] = useState<any[]>([]);

  const { user } = useUser();
  const { t } = useLanguage();

  // Filter warehouses based on user role - FOR ADMIN: show all warehouses, FOR WAREHOUSE: show only their warehouse
  const filteredWarehouses = user?.role === 'warehouse'
    ? warehouses.filter(warehouse => warehouse.code === user.warehouseCode)
    : warehouses;

  // Auto-select warehouse for warehouse users
  useEffect(() => {
    if (user?.role === 'warehouse' && user.warehouseCode && !newCustomer.destinationWarehouse) {
      setNewCustomer(prev => ({
        ...prev,
        destinationWarehouse: user.warehouseCode
      }));
    }
  }, [user, newCustomer.destinationWarehouse]);

  // Get unique countries and cities for filters
  const uniqueCountries = Array.from(new Set(customers.map(c => c.country))).filter(Boolean);
  const uniqueCities = Array.from(new Set(customers.map(c => c.city))).filter(Boolean);

  // Apply all filters
  const filteredCustomers = customers.filter(customer => {
    const searchMatch =
      customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.code.includes(searchTerm) ||
      customer.email.toLowerCase().includes(searchTerm.toLowerCase());

    const statusMatch = statusFilter === 'all' || customer.status === statusFilter;
    const countryMatch = countryFilter === 'all' || customer.country === countryFilter;
    const cityMatch = cityFilter === 'all' || customer.city === cityFilter;

    // Warehouse user filtering - only show customers assigned to their warehouse
    const warehouseMatch = user?.role === 'warehouse'
      ? (() => {
          // Check both camelCase and snake_case field names for compatibility
          const customerWarehouse = customer.destinationWarehouse || (customer as any).destination_warehouse;
          const destinationMatch = customerWarehouse === user.warehouseCode;

          return destinationMatch;
        })()
      : true;

    return searchMatch && statusMatch && countryMatch && cityMatch && warehouseMatch;
  }).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  // Reset all filters
  const resetFilters = () => {
    setStatusFilter('all');
    setCountryFilter('all');
    setCityFilter('all');
    setSearchTerm('');
  };

  // Get active filter count
  const activeFilterCount = [
    statusFilter !== 'all' ? 1 : 0,
    countryFilter !== 'all' ? 1 : 0,
    cityFilter !== 'all' ? 1 : 0,
    searchTerm.trim() !== '' ? 1 : 0
  ].reduce((sum, count) => sum + count, 0);

  const handleAddCustomer = () => {
    createCustomer({
      name: newCustomer.name,
      email: newCustomer.email,
      phone: newCustomer.phone,
      address: newCustomer.address,
      city: newCustomer.city,
      state: newCustomer.state,
      country: newCustomer.country,
      destination_warehouse: newCustomer.destinationWarehouse,
      status: 'Active'
    });

    setNewCustomer({
      name: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      country: '',
      destinationWarehouse: ''
    });
    setIsAddDialogOpen(false);
  };

  const handleEditCustomer = () => {
    if (!editingCustomer) return;

    updateCustomer({
      code: editingCustomer.code,
      name: editingCustomer.name,
      email: editingCustomer.email,
      phone: editingCustomer.phone,
      address: editingCustomer.address,
      city: editingCustomer.city,
      state: editingCustomer.state,
      country: editingCustomer.country,
      destination_warehouse: editingCustomer.destinationWarehouse,
      status: editingCustomer.status
    });

    setEditingCustomer(null);
    setIsEditDialogOpen(false);
  };

  const handleDeleteCustomer = (customerCode: string) => {
    deleteCustomer(customerCode);
  };

  const handleToggleStatus = (customerCode: string) => {
    const customer = customers.find(c => c.code === customerCode);
    if (!customer) return;

    updateCustomer({
      code: customer.code,
      status: customer.status === 'Active' ? 'Inactive' : 'Active'
    });
  };

  const handleDownloadPDF = (customer: Customer) => {
    // Create HTML content for PDF
    const pdfContent = `
<!DOCTYPE html>
<html>
<head>
    <title>Customer Information - ${customer.name}</title>
    <style>
        body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
        .header { color: #2563eb; border-bottom: 2px solid #2563eb; padding-bottom: 10px; margin-bottom: 20px; }
        .section { margin: 20px 0; }
        .label { font-weight: bold; color: #374151; }
        .value { margin-bottom: 5px; }
        .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; text-align: center; color: #6b7280; font-size: 12px; }
        .status-active { color: #16a34a; }
        .status-inactive { color: #dc2626; }
    </style>
</head>
<body>
    <div class="header">
        <h1>Customer Information</h1>
    </div>

    <div class="section">
        <div class="label">Customer Code:</div>
        <div class="value">${customer.code}</div>

        <div class="label">Name:</div>
        <div class="value">${customer.name}</div>

        <div class="label">Email:</div>
        <div class="value">${customer.email}</div>

        <div class="label">Phone:</div>
        <div class="value">${customer.phone}</div>

        <div class="label">Address:</div>
        <div class="value">${customer.address}, ${customer.city}, ${customer.state} ${customer.country}</div>

        <div class="label">Status:</div>
        <div class="value status-${customer.status.toLowerCase()}">${customer.status}</div>
    </div>

    <div class="footer">
        Generated on ${new Date().toLocaleDateString()} | SourceMyGoods - Shipping Management System
    </div>
</body>
</html>
    `;

    // Create and download the PDF
    const blob = new Blob([pdfContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `customer-${customer.code}-${customer.name.replace(/\s+/g, '-')}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handlePrint = (customer: Customer) => {
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Customer Information - ${customer.name}</title>
        <style>
          @page {
            size: A4;
            margin: 15mm;
          }
          
          @media print {
            body { 
              padding: 0;
              margin: 0;
            }
          }
        </style>
      </head>
      <body>
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
        <h1 style="color: #2563eb; border-bottom: 2px solid #2563eb; padding-bottom: 10px;">Customer Information</h1>
        <div style="margin: 20px 0;">
          <p><strong>Customer Code:</strong> ${customer.code}</p>
          <p><strong>Name:</strong> ${customer.name}</p>
          <p><strong>Email:</strong> ${customer.email}</p>
          <p><strong>Phone:</strong> ${customer.phone}</p>
          <p><strong>Address:</strong> ${customer.address}, ${customer.city}, ${customer.state} ${customer.country}</p>
          <p><strong>Status:</strong> <span style="color: ${customer.status === 'Active' ? '#16a34a' : '#dc2626'}">${customer.status}</span></p>
        </div>
        <div style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; text-align: center; color: #6b7280; font-size: 12px;">
          Generated on ${new Date().toLocaleDateString()} | SourceMyGoods - Shipping Management System
        </div>
      </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  const handleViewShipments = (customer: Customer) => {
    // Get real shipments from localStorage that belong to this customer
    const savedShipments = localStorage.getItem('shiphub-shipments');
    const allShipments = savedShipments ? JSON.parse(savedShipments) : [];

    // Filter shipments that belong to this customer
    const customerShipments = allShipments.filter((shipment: any) =>
      shipment && shipment.customerCode === customer.code
    );

    setSelectedCustomerShipments(customerShipments);
    setSelectedCustomerName(customer.name);
    setIsShipmentsDialogOpen(true);
  };

  const getStatusColor = (status: string) => {
    return status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('customers')}</h1>
            <p className="text-gray-600">{t('manageCustomerAccounts')}</p>
          </div>

          <div className="flex space-x-2">
            {/* View Toggle */}
            <div className="flex border rounded-md">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('grid')}
                className="rounded-r-none"
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                className="rounded-l-none"
              >
                <List className="h-4 w-4" />
              </Button>
            </div>

            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-1"
            >
              <Filter className="h-4 w-4" />
              <span>{t('filters')}</span>
              {activeFilterCount > 0 && (
                <Badge variant="secondary" className="ml-1">
                  {activeFilterCount}
                </Badge>
              )}
            </Button>

            <Dialog open={isAddDialogOpen} onValueChange={setIsAddDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Customer
                </Button>
              </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle>Add New Customer</DialogTitle>
                <DialogDescription>
                  Create a new customer account with a unique 4-digit code
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      id="name"
                      value={newCustomer.name}
                      onChange={(e) => setNewCustomer({...newCustomer, name: e.target.value})}
                      placeholder="Enter full name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={newCustomer.email}
                      onChange={(e) => setNewCustomer({...newCustomer, email: e.target.value})}
                      placeholder="Enter email address"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={newCustomer.phone}
                    onChange={(e) => setNewCustomer({...newCustomer, phone: e.target.value})}
                    placeholder="Enter phone number"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="address">Address</Label>
                  <Input
                    id="address"
                    value={newCustomer.address}
                    onChange={(e) => setNewCustomer({...newCustomer, address: e.target.value})}
                    placeholder="Enter street address"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="country">Country</Label>
                  <Select
                    value={newCustomer.country}
                    onValueChange={(value) => {
                      const states = getStatesForCountry(value);
                      setAvailableStates(states);
                      setAvailableCities([]);
                      setNewCustomer({...newCustomer, country: value, state: '', city: ''});
                    }}
                  >
                    <SelectTrigger id="country">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      {countries.map((country) => (
                        <SelectItem key={country.code} value={country.name}>
                          {country.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="state">State/Province</Label>
                    <Select
                      value={newCustomer.state}
                      onValueChange={(value) => {
                        const cities = getCitiesForState(newCustomer.country, value);
                        setAvailableCities(cities);
                        setNewCustomer({...newCustomer, state: value, city: ''});
                      }}
                      disabled={!newCustomer.country}
                    >
                      <SelectTrigger id="state">
                        <SelectValue placeholder={newCustomer.country ? "Select state" : "Select country first"} />
                      </SelectTrigger>
                      <SelectContent>
                        {availableStates.map((state, index) => (
                          <SelectItem key={index} value={state.name}>
                            {state.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="city">City</Label>
                    <Select
                      value={newCustomer.city}
                      onValueChange={(value) => setNewCustomer({...newCustomer, city: value})}
                      disabled={!newCustomer.state}
                    >
                      <SelectTrigger id="city">
                        <SelectValue placeholder={newCustomer.state ? "Select city" : "Select state first"} />
                      </SelectTrigger>
                      <SelectContent>
                        {availableCities.map((city, index) => (
                          <SelectItem key={index} value={city.name}>
                            {city.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="destination-warehouse">Destination Warehouse</Label>
                  <select
                    id="destination-warehouse"
                    value={newCustomer.destinationWarehouse}
                    onChange={(e) => setNewCustomer({...newCustomer, destinationWarehouse: e.target.value})}
                    className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
                  >
                    <option value="">-- Select destination warehouse --</option>
                    {warehouses
                      .filter(warehouse => warehouse.type !== 'Main')
                      .map((warehouse) => (
                        <option key={warehouse.code} value={warehouse.code}>
                          {warehouse.name} ({warehouse.code})
                        </option>
                      ))}
                  </select>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={handleAddCustomer}>Add Customer</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search customers by name, code, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Filters Section */}
        {showFilters && (
          <Card className="mt-4">
            <CardContent className="pt-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Filters</h3>
                <div className="flex space-x-2">
                  <Button variant="outline" size="sm" onClick={resetFilters}>
                    <X className="h-4 w-4 mr-1" />
                    Clear All
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setShowFilters(false)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="status-filter">Status</Label>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger id="status-filter">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="Active">Active</SelectItem>
                      <SelectItem value="Inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="country-filter">Country</Label>
                  <Select value={countryFilter} onValueChange={setCountryFilter}>
                    <SelectTrigger id="country-filter">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Countries</SelectItem>
                      {uniqueCountries.map(country => (
                        <SelectItem key={country} value={country}>
                          {country}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="city-filter">City</Label>
                  <Select value={cityFilter} onValueChange={setCityFilter}>
                    <SelectTrigger id="city-filter">
                      <SelectValue placeholder="Select city" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Cities</SelectItem>
                      {uniqueCities.map(city => (
                        <SelectItem key={city} value={city}>
                          {city}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Active Filters Display */}
              {activeFilterCount > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <h4 className="text-sm font-medium mb-2">Active Filters:</h4>
                  <div className="flex flex-wrap gap-2">
                    {statusFilter !== 'all' && (
                      <Badge variant="secondary" className="flex items-center space-x-1">
                        <Users className="h-3 w-3" />
                        Status: {statusFilter}
                      </Badge>
                    )}
                    {countryFilter !== 'all' && (
                      <Badge variant="secondary" className="flex items-center space-x-1">
                        <Globe className="h-3 w-3" />
                        Country: {countryFilter}
                      </Badge>
                    )}
                    {cityFilter !== 'all' && (
                      <Badge variant="secondary" className="flex items-center space-x-1">
                        <Building className="h-3 w-3" />
                        City: {cityFilter}
                      </Badge>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Customer Cards */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i} className="border-t-4 border-t-purple-500">
                <CardHeader>
                  <Skeleton className="h-6 w-3/4 mb-2" />
                  <Skeleton className="h-4 w-1/2" />
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-2/3" />
                    <Skeleton className="h-4 w-5/6" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredCustomers.length === 0 ? (
          <Card className="col-span-full border-dashed">
            <CardContent className="py-16 text-center">
              <Users className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Customers Found</h3>
              <p className="text-gray-500 mb-6">
                {searchTerm || activeFilterCount > 0
                  ? 'Try adjusting your search or filters'
                  : 'Get started by adding your first customer'}
              </p>
              {user?.role !== 'customer' && (
                <Button onClick={() => setIsAddDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add First Customer
                </Button>
              )}
            </CardContent>
          </Card>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCustomers.map((customer, index) => (
            <Card 
              key={customer.id} 
              className="hover:shadow-xl transition-all duration-300 border-t-4 border-t-purple-500 hover:scale-[1.02] group"
              style={{ animationDelay: `${index * 40}ms` }}
            >
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-lg truncate">{customer.name}</CardTitle>
                    <CardDescription className="flex items-center mt-1 flex-wrap gap-2">
                      <Badge className={`${getStatusColor(customer.status)} whitespace-nowrap`}>
                        {customer.status}
                      </Badge>
                      <span className="text-xs flex items-center gap-1">Code: <span className="font-mono font-bold">{customer.code}</span><CopyButton text={customer.code} label="Customer Code" size="icon" /></span>
                    </CardDescription>
                  </div>
                  <div className="flex space-x-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditingCustomer(customer);
                          setIsEditDialogOpen(true);
                        }}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteCustomer(customer.code)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggleStatus(customer.code)}
                      >
                        {customer.status === 'Active' ? (
                          <ToggleRight className="h-4 w-4 text-green-600" />
                        ) : (
                          <ToggleLeft className="h-4 w-4 text-red-600" />
                        )}
                      </Button>
                    </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm text-gray-600 min-w-0">
                    <Mail className="h-4 w-4 flex-shrink-0" />
                    <span className="truncate">{customer.email}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600 min-w-0">
                    <Phone className="h-4 w-4 flex-shrink-0" />
                    <span className="break-all">{customer.phone}</span>
                  </div>
                  <div className="flex items-start gap-2 text-sm text-gray-600 min-w-0">
                    <MapPin className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    <div className="break-words">
                      {customer.address}, {customer.city}, {customer.state} {customer.country}
                    </div>
                  </div>
                  {(() => {
                    const warehouseCode = customer.destinationWarehouse || (customer as any).destination_warehouse;
                    if (!warehouseCode) return null;
                    const warehouse = warehouses.find(w => w.code === warehouseCode);
                    return (
                      <div className="flex items-center text-sm text-gray-600">
                        <Building className="h-4 w-4 mr-2" />
                        <div>
                          Destination: {warehouse?.name || warehouseCode}
                        </div>
                      </div>
                    );
                  })()}
                  <div className="flex justify-between items-center pt-2 border-t">
                    <div className="flex items-center text-sm text-gray-500">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleViewShipments(customer)}
                        className="text-blue-600 hover:text-blue-800 mr-1"
                        title="View Shipments"
                      >
                        <Package className="h-4 w-4" />
                      </Button>
                      <span>{(() => {
                        try {
                          const savedShipments = localStorage.getItem('shiphub-shipments');
                          if (!savedShipments) return 0;

                          const shipmentsData = JSON.parse(savedShipments);
                          if (!Array.isArray(shipmentsData)) return 0;

                          return shipmentsData.filter((shipment: any) =>
                            shipment && shipment.customerCode === customer.code
                          ).length;
                        } catch (e) {
                          console.error('Error calculating customer shipment count:', e);
                          return 0;
                        }
                      })()} shipments</span>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-gray-400">
                        Since {customer.createdAt}
                      </div>
                      <div className="flex space-x-1 mt-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownloadPDF(customer)}
                          className="text-green-600 hover:text-green-800"
                          title="Download PDF"
                        >
                          Download
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handlePrint(customer)}
                          className="text-purple-600 hover:text-purple-800"
                          title="Print"
                        >
                          Print
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            ))}
          </div>
        ) : (
          /* List View */
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Warehouse</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredCustomers.map((customer) => (
                      <tr key={customer.id} className="hover:bg-gray-50">
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{customer.name}</div>
                              <div className="text-sm text-gray-500 font-mono">{customer.code}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm text-gray-900">{customer.email}</div>
                          <div className="text-sm text-gray-500">{customer.phone}</div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm text-gray-900">{customer.city}, {customer.state}</div>
                          <div className="text-sm text-gray-500">{customer.country}</div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {(() => {
                              const warehouseCode = customer.destinationWarehouse || (customer as any).destination_warehouse;
                              if (!warehouseCode) return '—';
                              const warehouse = warehouses.find(w => w.code === warehouseCode);
                              return warehouse?.name || warehouseCode;
                            })()}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <Badge className={getStatusColor(customer.status)}>
                            {customer.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewShipments(customer)}
                              title="View Shipments"
                            >
                              <Package className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setEditingCustomer(customer);
                                setIsEditDialogOpen(true);
                              }}
                              title="Edit"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleStatus(customer.code)}
                              title="Toggle Status"
                            >
                              {customer.status === 'Active' ? (
                                <ToggleRight className="h-4 w-4 text-green-600" />
                              ) : (
                                <ToggleLeft className="h-4 w-4 text-red-600" />
                              )}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* View Shipments Dialog */}
      <Dialog open={isShipmentsDialogOpen} onOpenChange={setIsShipmentsDialogOpen}>
        <DialogContent className="sm:max-w-[700px]">
          <DialogHeader>
            <DialogTitle>Shipments for {selectedCustomerName}</DialogTitle>
            <DialogDescription>
              View all shipments associated with this customer
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            {selectedCustomerShipments.length > 0 ? (
              <div className="space-y-3">
                {selectedCustomerShipments.map((shipment, index) => (
                  <div key={index} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex justify-between items-start">
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <Package className="h-4 w-4 text-blue-600" />
                          <span className="font-semibold">{shipment.shipmentNumber}</span>
                          <Badge className={
                            shipment.status === 'Delivered' ? 'bg-green-100 text-green-800' :
                            shipment.status === 'In Transit' ? 'bg-blue-100 text-blue-800' :
                            shipment.status === 'Processing' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }>
                            {shipment.status}
                          </Badge>
                        </div>
                        <div className="text-sm text-gray-600">
                          <span className="font-medium">Destination:</span> {shipment.destinationWarehouse}
                        </div>
                        <div className="text-sm text-gray-600">
                          <span className="font-medium">Created:</span> {shipment.createdAt}
                        </div>
                        <div className="text-sm text-gray-600">
                          <span className="font-medium">Trip:</span> {shipment.tripName || 'Unassigned'}
                        </div>
                      </div>
                      <div className="text-right space-y-2">
                        <div className="text-sm text-gray-600">
                          <span className="font-medium">Weight:</span> {(shipment.weight || 0)} kg
                        </div>
                        <div className="text-sm text-gray-600">
                          <span className="font-medium">Volume:</span> {(shipment.volume || 0)} m³
                        </div>
                        <div className="text-sm text-gray-600">
                          <span className="font-medium">Packages:</span> {(shipment.packages || 0)}
                        </div>
                        <div className="text-sm text-gray-600">
                          <span className="font-medium">Est. Delivery:</span> {shipment.estimatedDelivery || 'Not set'}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Shipments Found</h3>
                <p className="text-gray-500">This customer doesn't have any shipments yet.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setIsShipmentsDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Customer Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Customer</DialogTitle>
            <DialogDescription>
              Update customer information
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Full Name</Label>
                <Input
                  id="edit-name"
                  value={editingCustomer?.name || ''}
                  onChange={(e) => setEditingCustomer(prev => prev ? {...prev, name: e.target.value} : null)}
                  placeholder="Enter full name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-email">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={editingCustomer?.email || ''}
                  onChange={(e) => setEditingCustomer(prev => prev ? {...prev, email: e.target.value} : null)}
                  placeholder="Enter email address"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-phone">Phone</Label>
              <Input
                id="edit-phone"
                value={editingCustomer?.phone || ''}
                onChange={(e) => setEditingCustomer(prev => prev ? {...prev, phone: e.target.value} : null)}
                placeholder="Enter phone number"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-address">Address</Label>
              <Input
                id="edit-address"
                value={editingCustomer?.address || ''}
                onChange={(e) => setEditingCustomer(prev => prev ? {...prev, address: e.target.value} : null)}
                placeholder="Enter street address"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-city">City</Label>
                <Input
                  id="edit-city"
                  value={editingCustomer?.city || ''}
                  onChange={(e) => setEditingCustomer(prev => prev ? {...prev, city: e.target.value} : null)}
                  placeholder="City"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-state">State</Label>
                <Input
                  id="edit-state"
                  value={editingCustomer?.state || ''}
                  onChange={(e) => setEditingCustomer(prev => prev ? {...prev, state: e.target.value} : null)}
                  placeholder="State"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-country">Country</Label>
                <Input
                  id="edit-country"
                  value={editingCustomer?.country || ''}
                  onChange={(e) => setEditingCustomer(prev => prev ? {...prev, country: e.target.value} : null)}
                  placeholder="Country"
                />
              </div>
            </div>

            {/* Destination Warehouse Selection for Edit */}
            <div className="space-y-2">
              <Label htmlFor="edit-destination-warehouse">Destination Warehouse</Label>
              <Select
                value={editingCustomer?.destinationWarehouse || ''}
                onValueChange={(value) => {
                  setEditingCustomer(prev => prev ? {
                    ...prev,
                    destinationWarehouse: value
                  } : null);
                }}
                disabled={user?.role === 'warehouse'}
              >
                <SelectTrigger id="edit-destination-warehouse">
                  <SelectValue placeholder="Select destination warehouse" />
                </SelectTrigger>
                <SelectContent>
                  {filteredWarehouses
                    .filter(warehouse => warehouse.type !== 'Main')
                    .map((warehouse) => (
                      <SelectItem key={warehouse.code} value={warehouse.code}>
                        {warehouse.name} ({warehouse.code})
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              {user?.role === 'warehouse' && (
                <p className="text-sm text-gray-500">
                  Warehouse users can only assign customers to their assigned warehouse
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleEditCustomer}>Update Customer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {filteredCustomers.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No customers found</h3>
            <p className="text-gray-500 mb-4">
              {searchTerm ? 'No customers match your search criteria.' : 'Get started by adding your first customer.'}
            </p>
            {!searchTerm && (
              <Button onClick={() => setIsAddDialogOpen(true)}>
                <UserPlus className="h-4 w-4 mr-2" />
                Add Your First Customer
              </Button>
            )}
          </CardContent>
        </Card>
      )}
      </div>
    </Layout>
  );
}